import { useCallback, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useI18n } from '@runtime/core/i18n/i18n'
import { useCoach } from './CoachContext'
import { useCoachNavigation } from './CoachNavigation'
import { useCurrentCoachRules } from './CoachScreenRules'
import CoachRulesModal, { type CoachRulesKind } from './CoachRulesModal'
import styles from './CoachLocalActions.module.scss'

export default function CoachLocalActions() {
  const { t } = useI18n()
  const { route, setOpen } = useCoach()
  const navigation = useCoachNavigation()
  const screenRules = useCurrentCoachRules()
  const navigate = useNavigate()
  const [rulesOpen, setRulesOpen] = useState(false)
  const rulesButton = useRef<HTMLButtonElement>(null)
  const closeRules = useCallback(() => {
    setRulesOpen(false)
    rulesButton.current?.focus({ preventScroll: true })
  }, [])
  const kind = (['strategy', 'puzzle', 'online'] as CoachRulesKind[]).find(
    name => route === `/${name}` || route.startsWith(`/${name}/`),
  )
  const generalRules = ['/hub', '/home'].includes(route)

  const go = (destination: string) => {
    if (navigation && !navigation.mayNavigate()) return
    setOpen(false)
    navigate(destination)
  }

  return (
    <>
      <nav
        className={styles.actions}
        aria-label={t('coach.localActions.label')}
        data-testid="coach-local-actions"
      >
        <button type="button" onClick={() => go('/hub')} data-testid="coach-go-hub">
          {t('coach.localActions.hub')}
        </button>
        <button type="button" onClick={() => go('/home')} data-testid="coach-go-home">
          {t('coach.localActions.home')}
        </button>
        {(kind || generalRules || screenRules) && (
          <button
            ref={rulesButton}
            type="button"
            onClick={() => (kind || screenRules ? setRulesOpen(true) : go('/rules'))}
            data-testid="coach-open-rules"
          >
            {t(`coach.localActions.${screenRules ? 'screen' : (kind ?? 'general')}Rules`)}
          </button>
        )}
      </nav>
      {(kind || screenRules) && rulesOpen && (
        <CoachRulesModal kind={kind} content={screenRules?.()} open onClose={closeRules} />
      )}
    </>
  )
}
